import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { fetchMe, type MeResponse } from '../service/membership';
import { getTenantFromPath } from '../utils/tenant-utils';

export type MembershipContextProps = {
  me?: MeResponse;
  /** undefined until /membership/me has answered for the current tenant. */
  currentTenantAllowed?: boolean;
  reason?: 'not_member' | null;
  isLoading: boolean;
  refresh(): Promise<void>;
};

export const MembershipContext = createContext<MembershipContextProps | null>(null);

type Props = PropsWithChildren<{
  isAuthenticated: boolean;
  fetchJWT(options?: { redirectOnFail?: boolean }): Promise<{ idToken?: string }>;
}>;

/**
 * Loads /membership/me once per authenticated tenant. The first call after
 * sign-in is what binds a first-time user's membership row to their GRID sub.
 * The API enforces membership on every request anyway; this context only
 * decides whether to show the access-denied page instead of a broken one.
 */
export const MembershipProvider = ({ children, isAuthenticated, fetchJWT }: Props) => {
  const location = useLocation();
  const tenant = getTenantFromPath(location.pathname);
  const [state, setState] = useState<{ tenant: string | null; me?: MeResponse; isLoading: boolean }>({
    tenant: null,
    isLoading: false,
  });
  // The tenant whose lookup has started, whether or not it succeeded. One
  // attempt per tenant per sign-in: a failure must not be retried in a loop,
  // which would trip the API's rate limit and lock the user out.
  const attemptedFor = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!isAuthenticated || !tenant) return;
    attemptedFor.current = tenant;
    setState({ tenant, isLoading: true });
    try {
      const { idToken } = await fetchJWT({ redirectOnFail: false });
      if (!idToken) {
        setState({ tenant, isLoading: false });
        return;
      }
      const me = await fetchMe(idToken, tenant);
      setState({ tenant, me, isLoading: false });
    } catch (_error) {
      // Leave the decision to the API: the page renders and calls fail visibly.
      setState({ tenant, isLoading: false });
    }
  }, [fetchJWT, isAuthenticated, tenant]);

  useEffect(() => {
    if (!isAuthenticated) {
      attemptedFor.current = null;
      return;
    }
    if (!tenant || attemptedFor.current === tenant) return;
    void load();
  }, [isAuthenticated, load, tenant]);

  const value = useMemo<MembershipContextProps>(() => {
    const me = state.tenant === tenant ? state.me : undefined;
    const current = me?.currentTenant;
    return {
      me,
      currentTenantAllowed: current && current.tenant === tenant ? current.allowed : undefined,
      reason: current && current.tenant === tenant ? current.reason : undefined,
      isLoading: state.tenant === tenant && state.isLoading,
      refresh: load,
    };
  }, [load, state, tenant]);

  return <MembershipContext.Provider value={value}>{children}</MembershipContext.Provider>;
};

export const useMembership = (): MembershipContextProps => {
  const membership = useContext(MembershipContext);
  if (!membership) {
    throw Error('No MembershipProvider found.');
  }
  return membership;
};
